import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import Navbar from '../../components/storefront/layout/Navbar';
import Footer from '../../components/storefront/layout/Footer';
import HeroCarousel from '../../components/storefront/home/HeroCarousel';
import ProductSlider from '../../components/storefront/home/ProductSlider';
import ProductSliderSkeleton from '../../components/storefront/home/ProductSliderSkeleton';
import EmptyProductState from '../../components/storefront/home/EmptyProductState';
import { useGetStorefrontCategoriesQuery } from '../../store/slices/storefrontCategoriesSlice';
import {
  useGetFeaturedProductsQuery,
  useGetBestSellerProductsQuery,
  useGetOnSaleProductsQuery,
} from '../../store/slices/storefrontProductsSlice';
import { selectCartItemCount } from '../../store/slices/cartSlice';
import { getImageUrl, API_CONFIG } from '../../config/api';
import { useSEO } from '../../hooks/useSEO';

export default function HomePage() {
  useSEO({
    description: 'Shop premium quality nuts, dried fruits, and healthy snacks at The Nut Shop. Fresh products delivered to your door.',
  });

  // Fetch categories for navbar and footer
  const { data: categories = [], isLoading: categoriesLoading, error: categoriesError } =
    useGetStorefrontCategoriesQuery();

  // Fetch products for sliders
  const {
    data: featuredProducts = [],
    isLoading: featuredLoading,
    error: featuredError,
    refetch: refetchFeatured,
  } = useGetFeaturedProductsQuery();

  const {
    data: onSaleProducts = [],
    isLoading: onSaleLoading,
    error: onSaleError,
    refetch: refetchOnSale,
  } = useGetOnSaleProductsQuery();

  const {
    data: bestSellerProducts = [],
    isLoading: bestSellersLoading,
    error: bestSellersError,
    refetch: refetchBestSellers,
  } = useGetBestSellerProductsQuery();

  // Get cart item count for navbar
  const cartItemCount = useSelector(selectCartItemCount);

  // Show error toasts if any API calls fail
  useEffect(() => {
    if (featuredError) {
      toast.error('Failed to load featured products', {
        action: {
          label: 'Retry',
          onClick: () => refetchFeatured(),
        },
      });
    }
  }, [featuredError, refetchFeatured]);

  useEffect(() => {
    if (onSaleError) {
      toast.error('Failed to load sale products', {
        action: {
          label: 'Retry',
          onClick: () => refetchOnSale(),
        },
      });
    }
  }, [onSaleError, refetchOnSale]);

  useEffect(() => {
    if (bestSellersError) {
      toast.error('Failed to load best sellers', {
        action: {
          label: 'Retry',
          onClick: () => refetchBestSellers(),
        },
      });
    }
  }, [bestSellersError, refetchBestSellers]);

  // Hero carousel - fetch from API
  const [heroSlides, setHeroSlides] = useState<{
    id: string; imageUrl: string; heading: string; subheading: string;
    ctaText: string; ctaLink: string; buttonColor?: string;
  }[]>([]);
  const [heroLoading, setHeroLoading] = useState(true);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const res = await fetch(`${API_CONFIG.API_URL}/banner`);
        if (res.ok) {
          const data = await res.json();
          if (data.result?.length > 0) {
            setHeroSlides(data.result.map((b: any) => ({
              id: b.id,
              imageUrl: getImageUrl(b.imageUrl),
              heading: b.heading,
              subheading: b.subheading,
              ctaText: b.buttonText || 'Shop Now',
              ctaLink: b.buttonLink || '/products/best-sellers',
              buttonColor: b.buttonColor || undefined,
            })));
            setHeroLoading(false);
            return;
          }
        }
      } catch { /* fall through to defaults */ }
      // Fallback slides if no banners configured
      setHeroSlides([
        {
          id: '1',
          imageUrl: 'https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=1920&h=500&fit=crop',
          heading: 'Premium Quality Nuts',
          subheading: 'Discover our selection of fresh, organic nuts delivered to your door',
          ctaText: 'Shop Now',
          ctaLink: '/products/best-sellers',
        },
        {
          id: '2',
          imageUrl: 'https://images.unsplash.com/photo-1608797178974-15b35a64ede9?w=1920&h=500&fit=crop',
          heading: 'Special Offers',
          subheading: 'Save big on your favorite nuts and dried fruits',
          ctaText: 'Shop Now',
          ctaLink: '/products/best-sellers',
        },
        {
          id: '3',
          imageUrl: 'https://images.unsplash.com/photo-1613919113640-25732ec5e61f?w=1920&h=500&fit=crop',
          heading: 'Healthy Snacking',
          subheading: 'Nutritious and delicious options for every lifestyle',
          ctaText: 'Shop Now',
          ctaLink: '/products/best-sellers',
        },
      ]);
      setHeroLoading(false);
    };
    fetchBanners();
  }, []);

  if (categoriesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Navbar */}
      <Navbar
        categories={categories}
        cartItemCount={cartItemCount}
      />

      {/* Main Content - Add top padding to account for fixed navbar */}
      <main id="main-content" className="pt-navbar-offset">
        {/* Hero Carousel */}
        {!heroLoading && <HeroCarousel slides={heroSlides} />}

        {/* Featured Products Slider */}
        {featuredLoading ? (
          <ProductSliderSkeleton />
        ) : featuredError ? null : featuredProducts.length > 0 ? (
          <ProductSlider
            title="Featured Products"
            products={featuredProducts.map((product) => {
              const maxDiscount = Math.max(...(product.variants?.map(v => v.discountPercentage) || [0]));
              const price = product.minPrice ?? 0;
              return {
                id: product.id,
                name: product.name,
                imageUrl: getImageUrl(
                  product.images?.find((img) => img.isMain)?.thumbnailUrl ||
                  product.images?.find((img) => img.isMain)?.url ||
                  product.images?.[0]?.thumbnailUrl ||
                  product.images?.[0]?.url
                ),
                price,
                originalPrice: maxDiscount > 0 && price > 0
                  ? price / (1 - maxDiscount / 100)
                  : undefined,
                discountPercentage: maxDiscount > 0 ? maxDiscount : undefined,
                isFeatured: product.isFeatured,
                isBestSeller: product.isBestSeller,
                isOutOfStock: product.isOutOfStock,
                defaultVariantId: product.defaultVariantId,
                hasMultipleVariants: (product.variants?.length ?? 0) > 1,
                hasOptionGroups: (product.optionGroups?.length ?? 0) > 0,
              };
            })}
            viewAllLink="/products/featured"
          />
        ) : null}

        {/* On Sale Products Slider */}
        {onSaleLoading ? (
          <ProductSliderSkeleton />
        ) : onSaleError ? null : onSaleProducts.length > 0 ? (
          <ProductSlider
            title="Special Offers"
            products={onSaleProducts.map((product) => {
              const maxDiscount = Math.max(...(product.variants?.map(v => v.discountPercentage) || [0]));
              const price = product.minPrice ?? 0;
              return {
                id: product.id,
                name: product.name,
                imageUrl: getImageUrl(
                  product.images?.find((img) => img.isMain)?.thumbnailUrl ||
                  product.images?.find((img) => img.isMain)?.url ||
                  product.images?.[0]?.thumbnailUrl ||
                  product.images?.[0]?.url
                ),
                price,
                originalPrice: maxDiscount > 0 && price > 0
                  ? price / (1 - maxDiscount / 100)
                  : undefined,
                discountPercentage: maxDiscount > 0 ? maxDiscount : undefined,
                isFeatured: product.isFeatured,
                isBestSeller: product.isBestSeller,
                isOutOfStock: product.isOutOfStock,
                defaultVariantId: product.defaultVariantId,
                hasMultipleVariants: (product.variants?.length ?? 0) > 1,
                hasOptionGroups: (product.optionGroups?.length ?? 0) > 0,
              };
            })}
            viewAllLink="/products/on-sale"
          />
        ) : null}

        {/* Best Sellers Slider */}
        {bestSellersLoading ? (
          <ProductSliderSkeleton />
        ) : bestSellersError ? null : bestSellerProducts.length > 0 ? (
          <ProductSlider
            title="Best Sellers"
            products={bestSellerProducts.map((product) => {
              const maxDiscount = Math.max(...(product.variants?.map(v => v.discountPercentage) || [0]));
              const price = product.minPrice ?? 0;
              return {
                id: product.id,
                name: product.name,
                imageUrl: getImageUrl(
                  product.images?.find((img) => img.isMain)?.thumbnailUrl ||
                  product.images?.find((img) => img.isMain)?.url ||
                  product.images?.[0]?.thumbnailUrl ||
                  product.images?.[0]?.url
                ),
                price,
                originalPrice: maxDiscount > 0 && price > 0
                  ? price / (1 - maxDiscount / 100)
                  : undefined,
                discountPercentage: maxDiscount > 0 ? maxDiscount : undefined,
                isFeatured: product.isFeatured,
                isBestSeller: product.isBestSeller,
                isOutOfStock: product.isOutOfStock,
                defaultVariantId: product.defaultVariantId,
                hasMultipleVariants: (product.variants?.length ?? 0) > 1,
                hasOptionGroups: (product.optionGroups?.length ?? 0) > 0,
              };
            })}
            viewAllLink="/products/best-sellers"
          />
        ) : null}

        {/* Empty State - Show if no products at all */}
        {!featuredLoading &&
          !onSaleLoading &&
          !bestSellersLoading &&
          !featuredError &&
          !onSaleError &&
          !bestSellersError &&
          featuredProducts.length === 0 &&
          onSaleProducts.length === 0 &&
          bestSellerProducts.length === 0 && (
            <EmptyProductState
              title="No products available yet"
              message="Check back soon for our amazing selection!"
            />
          )}
      </main>

      {/* Footer */}
      <Footer categories={categories} />
    </div>
  );
}
